import { useEffect, useState } from 'react'
import type { SprintActivityGameOptions } from '../../../pure-js/types/Sprint'
import { sanitizeSprintActivityGameOptions } from '../../../pure-js/libs/SprintActivityGameOptions'
import useAppState from './useAppState'
import { processHandlers } from '../apiLayer'
import type { ActivityWithSessions } from '../types/GeneralTypes'

interface UseSessionIdFacilitatorResult {
  data: string | undefined
  teamId: string | undefined
  sprintId: string | undefined
  activityId: string | undefined
  activityType: string | undefined
  scheduledStart: string | undefined
  numberOfLoops?: number
  customSlotLength?: number
  minTimeLeftToAllowRestart?: number
  finalReflectionTimeOut?: number
  isLoading: boolean
}

/**
 * Sort activities by parsed start date and drop invalid dates.
 *
 * @param {ActivityWithSessions[]} activities - Activities to sort.
 * @return {Array<{activity: ActivityWithSessions, start: Date, isValid: boolean}>} Sorted activity metadata.
 */
const sortActivitiesByStartDate = (activities: ActivityWithSessions[]) => {
  return activities
    .map((activity) => {
      const start = new Date(activity.startDate)
      return {
        activity,
        start,
        isValid: !Number.isNaN(start.getTime())
      }
    })
    .filter(({ isValid }) => isValid)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
}

/**
 * Select the closest upcoming activity, or the earliest valid one as fallback.
 *
 * @param {ActivityWithSessions[]} activities - Activities to inspect.
 * @return {{activity: ActivityWithSessions, start: Date, isValid: boolean} | undefined} Selected activity metadata.
 */
const selectClosestActivity = (activities: ActivityWithSessions[]) => {
  if (activities.length === 0) {
    return undefined
  }

  const sorted = sortActivitiesByStartDate(activities)
  if (sorted.length === 0) {
    return undefined
  }

  const now = Date.now()
  const upcoming = sorted.find(({ start }) => start.getTime() >= now)

  return upcoming ?? sorted[0]
}

/**
 * Load facilitator session context for the closest activity.
 *
 * @return {UseSessionIdFacilitatorResult} Session context for facilitator game launch.
 */
export const useSessionIdFacilitator = (): UseSessionIdFacilitatorResult => {
  const { state } = useAppState()
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [teamId, setTeamId] = useState<string | undefined>(undefined)
  const [sprintId, setSprintId] = useState<string | undefined>(undefined)
  const [activityId, setActivityId] = useState<string | undefined>(undefined)
  const [activityType, setActivityType] = useState<string | undefined>(undefined)
  const [scheduledStart, setScheduledStart] = useState<string | undefined>(undefined)
  const [runtimeConfig, setRuntimeConfig] = useState<SprintActivityGameOptions>({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    const fetchUpcomingActivity = async () => {
      try {
        const response = await processHandlers.getUpcomingActivitiesListForGamer(state)

        if (!isMounted) {
          return
        }

        if (!response.success || !response.data || response.data.length === 0) {
          setIsLoading(false)
          return
        }

        const selected = selectClosestActivity(response.data as ActivityWithSessions[])

        if (!selected) {
          setIsLoading(false)
          return
        }

        const { activity, start } = selected

        setSprintId(activity.sprintId)
        setActivityId(activity.id)
        setActivityType(activity.type)
        setScheduledStart(start.toISOString())
        // Preserve undefined values here so legacy activities continue using Unity-side defaults.
        setRuntimeConfig(sanitizeSprintActivityGameOptions(activity))

        const matchedSession = activity.sessions?.[0]

        if (matchedSession) {
          setSessionId(matchedSession.id)
          setTeamId(matchedSession.teamId)
        } else {
          setSessionId(undefined)
          setTeamId(undefined)
        }
      } catch (error) {
        console.error('Failed to fetch facilitator session context', error)
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchUpcomingActivity()

    return () => {
      isMounted = false
    }
  }, [state])

  return {
    data: sessionId,
    teamId,
    sprintId,
    activityId,
    activityType,
    scheduledStart,
    ...runtimeConfig,
    isLoading
  }
}

export default useSessionIdFacilitator
